import { skipToken } from "@reduxjs/toolkit/query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Avatar, Button, Icon, Modal, Spinner, Tooltip, Typography } from "@/components/atoms";
import { StepIndicator } from "@/components/molecules/StepIndicator/StepIndicator";
import { useGetClientBookingDataQuery, useImportBulkBookingMutation } from "@/redux/apis/clientBooking/clientBookingApi";
import { useGetClientPaymentMethodsQuery } from "@/redux/apis/clientPayment/clientPaymentApi";
import { useAppSelector } from "@/redux/hooks";
import { accountIdSelector } from "@/redux/slices/account/selectors";
import { addToast, clsx, getInitials } from "@/utils";
import { BulkBookingClient, BulkBookingProvider, BulkBookingStep, useBulkBooking } from "./BulkBookingContext";
import { BulkImportTracker } from "./BulkImportTracker";
import { preferredPaymentMethod } from "./BulkPaymentMethod";
import { BOOKING_FIELDS, toValidationRows, unmappedRequiredFields } from "./bookingFields";
import { canImport, resolveImportSelection } from "./importSelection";
import { RowEdits } from "./steps/EditRowModal";
import { MatchColumnsStep } from "./steps/MatchColumnsStep";
import { ReviewStep } from "./steps/ReviewStep";
import { SelectHeaderStep } from "./steps/SelectHeaderStep";
import { UploadStep } from "./steps/UploadStep";
import { useBulkValidation } from "./useBulkValidation";

/**
 * Shown instead of the wizard when the account has no payment method at all.
 *
 * The booking sheet gates Book Trip on the same condition, so such an account can't book by hand
 * either — and the server would reject every row with "Default payment method not set". Refusing at
 * open costs the user nothing; refusing at import costs them a mapped file and fifty resolved
 * addresses first.
 *
 * Invoice clients are unaffected: an invoice profile IS a payment method, so only genuinely
 * unconfigured accounts land here.
 */
const NoPaymentMethod = ({ clientName }: { clientName?: string }) => (
  <div className="flex flex-col items-center gap-3 py-10 text-center">
    <Icon name="EmptyWallet" variant="Bold" size="xl" className="text-neutral-dark-gray" />
    <Typography variant="title" className="text-neutral-black">
      No payment method on file
    </Typography>
    <Typography variant="paragraph" className="max-w-md text-neutral-dark-gray">
      {clientName
        ? `${clientName} needs a payment method before bookings can be imported. Add one from their payment settings, then try again.`
        : "Add a payment method from your payment settings before importing bookings."}
    </Typography>
  </div>
);

interface BulkBookingModalProps {
  open: boolean;
  onClose: () => void;
  /** The client being booked for — operator and EA flows pick one first; omitted for self-service. */
  client?: BulkBookingClient;
}

const STEP_COPY: Record<BulkBookingStep, { title: string; description: string }> = {
  upload: {
    title: "Upload CSV File",
    description:
      // Names all three rates the row service rejects. It listed only hourly and daily, so an operator
      // with a fixed-price transfer type got no warning until step 3 turned them down row by row.
      "Import your bookings in bulk using a .csv or Excel file. If you need to add extra stops or arrange an hourly, daily or fixed-price hire, you can create those bookings individually.",
  },
  "select-header": {
    title: "Select Header Row",
    description: "Choose which row in your file contains the column names. This helps us correctly identify your data.",
  },
  "match-columns": {
    title: "Match Columns",
    description: "Confirm how your file's columns align with RideMinder's booking fields",
  },
  review: {
    title: "Review and Pricing",
    description:
      "Check each booking's details and estimated fare before importing. You can fix any missing or invalid details below. Once all errors are resolved, you'll be able to import your bookings.",
  },
};

/** The numbered steps. "upload" is deliberately absent — the design shows no indicator on it. */
const INDICATED_STEPS: BulkBookingStep[] = ["select-header", "match-columns", "review"];

const BulkBookingWizard = ({ open, onClose, onQueued }: Omit<BulkBookingModalProps, "client"> & { onQueued: (bulkId: string) => void }) => {
  const { step, client, goTo, canProceedFromMapping, dataRows, mapping, editCell, file, headerRowIndex, workbook } = useBulkBooking();
  const [importRows, { isLoading: isImporting }] = useImportBulkBookingMutation();

  // The operator's transfer types, vehicles and extras — the same list the booking sheet offers, so
  // the edit modal's pickers match what a manual booking would show. Self-service scopes to the
  // logged-in account; operator and EA imports scope to the picked client.
  const ownAccountId = useAppSelector(accountIdSelector);
  const scopedAccountId = client?.accountId ?? ownAccountId;
  const { data: bookingData } = useGetClientBookingDataQuery(scopedAccountId ? { accountId: scopedAccountId } : skipToken);

  // Every row is priced against, and charged to, one card. Fetched at open rather than at the review
  // step: an account with none can't book at all, and finding that out after fifty addresses have
  // been resolved at Google's expense is the worst possible moment.
  const { data: paymentMethods = [], isLoading: loadingPaymentMethods } = useGetClientPaymentMethodsQuery(scopedAccountId || skipToken);
  // Default-flagged, else the first — the same preselect PaymentPanel runs, and what the server falls
  // back to. Not selectable: the wizard always uses the account default, which step 2 states outright.
  // Sent explicitly all the same, so the estimate and the import pin to one card even if the account
  // default changes mid-session, and so the parent's own [0] fallback never picks a different one.
  const paymentMethod = preferredPaymentMethod(paymentMethods);
  const hasNoPaymentMethod = Boolean(scopedAccountId) && !loadingPaymentMethods && paymentMethods.length === 0;

  /**
   * Rows the user has explicitly UNticked — the inverse of what you'd expect, and deliberately so.
   *
   * Someone who uploaded fifty bookings wants fifty bookings; making them tick fifty boxes to say so
   * is backwards. Storing exclusions means a row is ticked from its very first render, so rows
   * streaming in a chunk at a time never flash unticked before an effect catches up.
   *
   * Rows with errors are ticked too. That's what disables the import button, so a broken row has to
   * be fixed or explicitly unticked — importing 47 of 50 and leaving the user to notice the
   * shortfall themselves is the failure mode being avoided.
   */
  const [unticked, setUnticked] = useState<Set<number>>(new Set());
  /**
   * Rows dropped from this import for good. Separate from `unticked` because discarding removes the
   * row from the table, where unticking only skips it — and unlike unticking, it can't be undone
   * without going back and re-mapping.
   */
  const [discarded, setDiscarded] = useState<Set<number>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  /** "Leave Bulk Booking Upload?" — see `requestClose`. */
  const [confirmingLeave, setConfirmingLeave] = useState(false);

  const rows = useMemo(() => toValidationRows(dataRows, mapping), [dataRows, mapping]);
  /** Named in both the step's banner and the Next tooltip, from one shared source. */
  const unmappedRequired = useMemo(() => unmappedRequiredFields(mapping), [mapping]);
  // Only starts once the review step is reached — validating in the background would spend real
  // geocoding and pricing calls on a file the user may still be re-mapping.
  // Changes only when the underlying file, header row or mapping changes — never on an inline edit,
  // so fixing one row doesn't re-resolve and re-price the whole file.
  const sourceKey = useMemo(
    () => `${file?.name ?? ""}:${file?.size ?? 0}:${headerRowIndex}:${JSON.stringify(mapping)}`,
    [file, headerRowIndex, mapping]
  );
  const validation = useBulkValidation({
    rows,
    accountId: client?.accountId,
    enabled: step === "review",
    sourceKey,
    paymentMethodUuid: paymentMethod?.uuid,
  });

  const copy = STEP_COPY[step];
  const indicatorPosition = INDICATED_STEPS.indexOf(step);

  /** Everything still in play — discarded rows are gone from the table and from the import. */
  const liveRows = useMemo(() => validation.validated.filter((row) => !discarded.has(row.row_index)), [validation.validated, discarded]);

  const { selected, importableCount, blockingCount } = useMemo(() => resolveImportSelection(liveRows, unticked), [liveRows, unticked]);

  const toggleRow = useCallback((rowIndex: number) => {
    setUnticked((current) => {
      const next = new Set(current);
      if (!next.delete(rowIndex)) next.add(rowIndex);
      return next;
    });
  }, []);

  const toggleAll = useCallback((rowIndexes: number[]) => {
    // All ticked → untick the lot; anything else → tick the lot.
    setUnticked((current) => (rowIndexes.every((index) => !current.has(index)) ? new Set(rowIndexes) : new Set()));
  }, []);

  // Re-mapping or re-uploading throws the old results away, so the ticks go with them — otherwise
  // row 3 of the new file inherits whatever row 3 of the old one was set to.
  useEffect(() => {
    setUnticked(new Set());
    setDiscarded(new Set());
  }, [sourceKey]);

  const discardSelected = useCallback(() => {
    setDiscarded((current) => new Set([...current, ...selected]));
    setConfirmingDiscard(false);
  }, [selected]);

  /**
   * Queue the ticked rows and get out of the way.
   *
   * One POST — the server stores the reviewed payloads, hands the work to a queue and answers
   * straight away. The browser used to drive this itself, looping chunked requests it had to stay
   * open for; a hundred bookings is minutes of work, and closing the tab left some created with no
   * record of which. Now the tab only has to survive one request.
   *
   * Progress moves to the Snackbar, re-issued under the same toast id so it updates in place rather
   * than stacking. Close the tab and the worker carries on regardless — the completion email is what
   * covers that, not this.
   */
  const runImport = useCallback(async () => {
    // Each row goes up with the values the user actually mapped alongside the payload built from
    // them. The payload is machine-shaped — resolved place ids, a numeric vehicle, a combined
    // datetime — and there's no way back from it to something a person can edit. Without the source,
    // a row that failed because six passengers won't fit in a sedan could only be retried unchanged.
    const sourceByIndex = new Map(rows.map((row) => [row.row_index, row]));

    const payloads = liveRows
      .filter((row) => selected.has(row.row_index) && row.payload)
      .map((row) => ({
        row_index: row.row_index,
        payload: row.payload,
        source: sourceByIndex.get(row.row_index) ?? { row_index: row.row_index },
      }));

    try {
      const queued = await importRows({
        rows: payloads,
        accountId: client?.accountId,
        paymentMethodUuid: paymentMethod?.uuid,
        fileName: file?.name,
      }).unwrap();

      setConfirming(false);
      onQueued(queued.bulk_id);
      onClose();
    } catch {
      // Nothing was queued, so nothing was booked — the wizard stays put with everything intact and
      // the user can simply press the button again.
      setConfirming(false);
      addToast("danger", "We couldn't start the import. Check your connection and try again.");
    }
  }, [liveRows, rows, selected, importRows, client?.accountId, paymentMethod?.uuid, file?.name, onClose, onQueued]);

  /**
   * Closing, but only after asking — once there is anything to lose.
   *
   * A click anywhere outside this modal used to discard the lot: the file, the header row, the column
   * mapping, every inline correction, and a review step that had just spent a Google lookup on each
   * address and a price on each row. All of it gone to a misplaced click, with no way back but
   * uploading again.
   *
   * Gated on a workbook existing, so someone who opens the wizard and thinks better of it is not made
   * to confirm leaving an empty screen.
   */
  const requestClose = useCallback(() => {
    if (!workbook) {
      onClose();
      return;
    }

    setConfirmingLeave(true);
  }, [workbook, onClose]);

  /** Apply a row's edits, then re-check just that row. */
  const applyRowEdits = useCallback(
    (rowIndex: number, edits: RowEdits) => {
      BOOKING_FIELDS.forEach((field) => {
        const value = edits.values[field.key];
        if (value !== undefined) editCell(rowIndex, field.key, value);
      });

      const row = BOOKING_FIELDS.reduce((acc, field) => ({ ...acc, [field.key]: edits.values[field.key] ?? "" }), { row_index: rowIndex });

      validation.revalidateRow(row, edits.resolved);
    },
    [editCell, validation]
  );

  return (
    <>
      <Modal
        open={open}
        // The confirmation below is a sibling dialog, not a DOM descendant, so Headless UI counts a
        // click inside it as a click OUTSIDE this one and fired this onClose too — Cancel closed the
        // whole wizard. Also guards against dismissing mid-import, which would leave the user unsure
        // what got booked.
        onClose={() => {
          if (confirming || confirmingDiscard || confirmingLeave || isImporting) return;
          requestClose();
        }}
        variant="web-large"
        // web-large caps at max-w-4xl, which squeezes the wizard's 13- and 14-column tables. Those
        // steps take whatever the screen gives them rather than a fixed ceiling — on a wide monitor
        // that's the difference between reading the table and scrolling it sideways. Upload has no
        // table and is pinned narrow by the design.
        className={clsx(step === "upload" ? "!max-w-[550px]" : "!max-w-[95vw]")}
        // Every step carries the client's name so an operator juggling several never loses track of
        // whose trips they're importing.
        title={client ? `${copy.title} (${client.name})` : copy.title}
        description={copy.description}
        // Top-right, level with the title, per Figma. It reads as chrome rather than content, so it
        // belongs in the header rather than pushing the step's body down.
        headerAction={
          indicatorPosition >= 0 ? <StepIndicator numSteps={INDICATED_STEPS.length} currentStep={indicatorPosition + 1} /> : undefined
        }
        renderButtons={() => (
          <>
            {client && (
              <span className="mr-auto flex items-center gap-2 rounded-full border border-neutral-mid-gray py-1 pl-1 pr-3">
                <Avatar size="xs" src={client.avatarSrc ?? undefined} alt={client.name} className="bg-neutral-mid-gray">
                  {!client.avatarSrc && (
                    <Typography variant="small" className="text-neutral-dark-gray">
                      {getInitials(client.name)}
                    </Typography>
                  )}
                </Avatar>
                <Typography variant="small" className="text-neutral-black">
                  For {client.name}
                </Typography>
              </span>
            )}

            {hasNoPaymentMethod ? (
              <Button size="md" onClick={onClose}>
                Close
              </Button>
            ) : (
              <>
                {/* Cancel goes through the same confirmation as a click outside — it discards exactly
                    the same work, and only one of the two being guarded would be arbitrary. */}
                <Button variant="tertiary" size="md" onClick={requestClose} disabled={isImporting}>
                  {step === "upload" ? "Close" : "Cancel"}
                </Button>
                {step === "select-header" && (
                  <Button size="md" endIcon="ArrowRight" onClick={() => goTo("match-columns")}>
                    Next
                  </Button>
                )}
                {step === "match-columns" && (
                  <>
                    <Button variant="tertiary" size="md" startIcon="ArrowLeft" onClick={() => goTo("select-header")}>
                      Back
                    </Button>
                    {/* Same wrapper trick as Import Bookings: a disabled <button> emits no pointer
                        events, so Tippy has to bind to a span or the tooltip never fires in the one
                        state that needs explaining. */}
                    <Tooltip
                      disabled={canProceedFromMapping}
                      content={`Match ${
                        unmappedRequired.length === 1 ? "one more field" : `${unmappedRequired.length} more fields`
                      } to continue: ${unmappedRequired.map((field) => field.label).join(", ")}.`}
                    >
                      <span className="inline-flex">
                        <Button size="md" endIcon="ArrowRight" disabled={!canProceedFromMapping} onClick={() => goTo("review")}>
                          Next
                        </Button>
                      </span>
                    </Tooltip>
                  </>
                )}
                {step === "review" && (
                  <>
                    <Button variant="tertiary" size="md" startIcon="ArrowLeft" onClick={() => goTo("match-columns")} disabled={isImporting}>
                      Back
                    </Button>
                    {/* Tippy binds its listeners to the child, and a disabled <button> emits no
                        pointer events — so the tooltip hangs off a wrapping span, or it would never
                        fire in exactly the state that needs explaining. */}
                    <Tooltip
                      disabled={!validation.isValidating && blockingCount === 0}
                      content={
                        validation.isValidating
                          ? `Still checking — ${validation.completed} of ${validation.total} done.`
                          : `${blockingCount} selected ${blockingCount === 1 ? "booking has" : "bookings have"} errors. Fix ${
                              blockingCount === 1 ? "it" : "them"
                            }, or untick to import the rest.`
                      }
                    >
                      <span className="inline-flex">
                        {/*
                         * Held until every row has been checked.
                         *
                         * `importableCount` counts only rows that have come BACK, so on a hundred-row
                         * file the first clean chunk of ten made this live while ninety were still
                         * unchecked. Pressing it then queued ten bookings and silently dropped the
                         * rest — and the confirmation dutifully said "import 10 bookings", which reads
                         * as correct unless you noticed the number was still climbing.
                         */}
                        <Button
                          size="md"
                          disabled={!canImport({ isValidating: validation.isValidating, importableCount, blockingCount, isImporting })}
                          onClick={() => setConfirming(true)}
                        >
                          Import Bookings
                        </Button>
                      </span>
                    </Tooltip>
                  </>
                )}
              </>
            )}
          </>
        )}
      >
        <div className="flex flex-col gap-6">
          {hasNoPaymentMethod ? (
            <NoPaymentMethod clientName={client?.name} />
          ) : (
            <>
              {step === "upload" && <UploadStep />}
              {step === "select-header" && <SelectHeaderStep paymentMethod={paymentMethod} />}
              {step === "match-columns" && <MatchColumnsStep />}
              {step === "review" && (
                <ReviewStep
                  validation={validation}
                  selected={selected}
                  onToggleRow={toggleRow}
                  onToggleAll={toggleAll}
                  discarded={discarded}
                  onDiscard={() => setConfirmingDiscard(true)}
                  bookingData={bookingData}
                  onEditRow={applyRowEdits}
                />
              )}
            </>
          )}
        </div>
      </Modal>

      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        variant="web-small"
        // 400px with evenly split lg buttons, matching the reset-mappings confirmation.
        className="sm:w-[400px]"
        title="Confirm Import"
        description={`Are you sure you want to import ${importableCount} ${importableCount === 1 ? "booking" : "bookings"}?`}
        renderButtons={() => (
          <>
            <Button variant="tertiary" size="lg" className="flex-1" disabled={isImporting} onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            {/* Real bookings are being created a chunk at a time, which takes seconds — the dialog
                stays up with a spinner rather than vanishing into a silent wizard. */}
            <Button
              size="lg"
              className="flex-1"
              disabled={isImporting}
              startIcon={isImporting ? <Spinner variant="light" /> : undefined}
              onClick={runImport}
            >
              {isImporting ? "Importing…" : "Import Bookings"}
            </Button>
          </>
        )}
      >
        {/* Its own paragraph rather than tacked onto the description, as the design has it — Modal
            renders `description` as a single block. */}
        <Typography variant="paragraph" className="text-neutral-dark-gray">
          Please make sure all booking details are correct before continuing.
        </Typography>
      </Modal>

      <Modal
        open={confirmingLeave}
        onClose={() => setConfirmingLeave(false)}
        variant="web-small"
        // 400px with evenly split lg buttons, matching the other two confirmations in this wizard.
        className="sm:w-[400px]"
        title="Leave Bulk Booking Upload?"
        description="You're currently uploading a bulk booking. If you leave now, any changes you've made won't be saved. Are you sure you want to leave this page?"
        renderButtons={() => (
          <>
            <Button variant="tertiary" size="lg" className="flex-1" onClick={() => setConfirmingLeave(false)}>
              Don&apos;t Leave
            </Button>
            <Button
              variant="custom"
              size="lg"
              className="flex-1 bg-danger text-white hover:bg-danger/90"
              onClick={() => {
                setConfirmingLeave(false);
                onClose();
              }}
            >
              Leave Bulk Booking
            </Button>
          </>
        )}
      />

      <Modal
        open={confirmingDiscard}
        onClose={() => setConfirmingDiscard(false)}
        variant="web-small"
        className="sm:w-[400px]"
        title={`Discard ${selected.size} ${selected.size === 1 ? "booking" : "bookings"}?`}
        description="They'll be removed from this import. To get them back you'd have to go back and re-map the file."
        renderButtons={() => (
          <>
            <Button variant="tertiary" size="lg" className="flex-1" onClick={() => setConfirmingDiscard(false)}>
              Cancel
            </Button>
            <Button variant="custom" size="lg" className="flex-1 bg-danger text-white hover:bg-danger/90" onClick={discardSelected}>
              Yes, Discard
            </Button>
          </>
        )}
      />
    </>
  );
};

/**
 * The bulk booking wizard (DEV-1215) — Upload → Select Header Row → Match Columns → Review and
 * Pricing. Shared by the client, operator and EA portals; the only difference between them is
 * whether a `client` is passed, which scopes the import and stamps every step's title.
 *
 * The provider is remounted with the modal (`key`), so closing and reopening starts clean rather
 * than resuming someone else's half-finished import.
 */
export const BulkBookingModal = ({ open, onClose, client }: BulkBookingModalProps) => {
  /**
   * The queued import currently being followed.
   *
   * Held HERE rather than in the wizard. The provider below is keyed on `open`, so submitting
   * unmounts the wizard and everything it knew — but the import is still running, and something has
   * to be left watching it.
   */
  const [trackingId, setTrackingId] = useState<string>();

  return (
    <>
      <BulkBookingProvider key={open ? `${client?.accountId ?? "self"}-open` : "closed"} client={client}>
        <BulkBookingWizard open={open} onClose={onClose} onQueued={setTrackingId} />
      </BulkBookingProvider>

      <BulkImportTracker bulkId={trackingId} accountId={client?.accountId} onSettled={() => setTrackingId(undefined)} />
    </>
  );
};
